import { Chip, Collapse, Paper, Stack, Typography } from '@mui/material'
import { C1Button, spacing } from 'c1ui'
import { C1BorderRadius } from 'c1ui/theme/C1BorderRadius'
import { shadows } from 'c1ui/theme/C1Theme'
import { ReadOnlyCodeViewer } from 'components/core/editor/ReadOnlyCodeViewer'
import { C1Icon } from 'components/core/icons/C1Icon'
import { SafeMarkdown } from 'components/core/markdown'
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react'
import { FC, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

type FunctionActionItemProps = {
    functionId: string
    displayName: string
    description: string
    explanation: string
    files: { [key: string]: string }
    onApply?: () => void
}

/**
 * Renders a function draft result from the AI assistant.
 * Shows the function header, explanation, a collapsible code preview, and an "Apply to editor" button.
 */
export const FunctionActionItem: FC<FunctionActionItemProps> = ({
    displayName,
    explanation,
    files,
    onApply,
}) => {
    const intl = useIntl()
    const [previewOpen, setPreviewOpen] = useState(false)

    const fileEntries = Object.entries(files).sort(([a], [b]) => {
        if (a === 'main.ts') return -1
        if (b === 'main.ts') return 1
        return a.localeCompare(b)
    })
    const hasCode = fileEntries.some(([, content]) => content.length > 0)

    if (!displayName && !onApply) {
        return null
    }

    return (
        <Paper
            variant="outlined"
            sx={{
                p: spacing.l,
                bgcolor: 'common.white',
                borderRadius: C1BorderRadius.large,
                boxShadow: shadows.default,
            }}
        >
            <Stack gap={spacing.l}>
                {/* Header chip */}
                <Chip
                    label={intl.formatMessage({
                        id: 'function_chip_label',
                        defaultMessage: 'Function',
                    })}
                    size="small"
                    color="secondary"
                    variant="outlined"
                    sx={{ alignSelf: 'flex-start' }}
                />

                {/* Display name */}
                <Typography variant="h6" component="div">
                    {displayName || intl.formatMessage({
                        id: 'copilot_function_untitled',
                        defaultMessage: 'Untitled function',
                    })}
                </Typography>

                {/* Agent explanation */}
                {explanation && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        component="div"
                        sx={{
                            '& p': { margin: 0 },
                            '& p:not(:last-child)': { mb: spacing.xs },
                        }}
                    >
                        <SafeMarkdown>{explanation}</SafeMarkdown>
                    </Typography>
                )}

                {/* Code preview toggle */}
                {hasCode && (
                    <>
                        <C1Button
                            variant="text"
                            onClick={(): void => setPreviewOpen((prev) => !prev)}
                            startIcon={<C1Icon icon={previewOpen ? ChevronDown : ChevronRight} />}
                            sx={{
                                alignSelf: 'flex-start',
                                color: 'text.secondary',
                                textTransform: 'none',
                            }}
                        >
                            <FormattedMessage
                                id="copilot_function_preview_code"
                                defaultMessage="Preview ({count} {count, plural, one {file} other {files}})"
                                values={{ count: fileEntries.length }}
                            />
                        </C1Button>
                        <Collapse in={previewOpen}>
                            <Stack gap={spacing.s}>
                                {fileEntries.map(([filename, content]) => (
                                    <Stack key={filename} gap={spacing.xs}>
                                        {fileEntries.length > 1 && (
                                            <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                                                {filename}
                                            </Typography>
                                        )}
                                        <ReadOnlyCodeViewer
                                            value={content}
                                            language="typescript"
                                            maxHeight="18.75rem"
                                        />
                                    </Stack>
                                ))}
                            </Stack>
                        </Collapse>
                    </>
                )}

                {/* Apply to editor button */}
                {onApply && (
                    <C1Button
                        variant="contained"
                        color="primary"
                        onClick={onApply}
                        startIcon={<C1Icon icon={ExternalLink} />}
                        sx={{ alignSelf: 'flex-start' }}
                    >
                        <FormattedMessage
                            id="copilot_function_apply"
                            defaultMessage="Apply to editor"
                        />
                    </C1Button>
                )}
            </Stack>
        </Paper>
    )
}
